import { QueryClient } from "@tanstack/react-query";

import { DeliveryStatus, ManualOrderFormData, Order, OrderList, OrderNote, OrderStatus, PayType, PaymentStatus } from "../types/order";
import { ReportExtra } from "../types/report";
import { ShipTypeOption } from "../types/shipSetting";
import { get, post, postFormData } from "./fetch.core";
import { Translation } from "./translation.core";
import { getLoginToken } from "./login.core";
import { getShopId } from "./shop.core";

export const OrderStatusArr: Array<OrderStatus> = ["pending", "processing", "shipped", "delivered", "cancelled", "returned"];
export const PaymentStatusArr: Array<PaymentStatus> = ["awaiting", "paid", "refunded"];
export const DeliveryStatusArr: Array<DeliveryStatus> = ["scheduled", "preparing", "onDelivery", "canPickup", "delivered", "cancelled"];
export const GroupByArr: Array<ReportExtra> = ["order", "date", "customer"]; //, "product"];

interface LoadOrderParams {
  paging: {
    size: number;
    page: number;
  };
  userIds: string[];
  orderStatuses: OrderStatus[];
  paymentStatuses: PaymentStatus[];
  deliveryStatuses: DeliveryStatus[];
  search: string;
  date: {
    from: string;
    to: string;
  };
  orderNumber: string;
  productId: string;
  shipType: ShipTypeOption;
}

async function loadOrders(params: Partial<LoadOrderParams>) {
  if (params.orderStatuses) {
    if (params.orderStatuses.length === 0) delete params.orderStatuses;
  }

  // looks like this is a react form hook bug
  if ((params.orderStatuses as any) === false) {
    delete params.orderStatuses;
  }

  if (params.paymentStatuses) {
    if (params.paymentStatuses.length === 0) delete params.paymentStatuses;
  }

  // looks like this is a react form hook bug
  if ((params.paymentStatuses as any) === false) {
    delete params.paymentStatuses;
  }

  if (params.deliveryStatuses) {
    if (params.deliveryStatuses.length === 0) delete params.deliveryStatuses;
  }

  // looks like this is a react form hook bug
  if ((params.deliveryStatuses as any) === false) {
    delete params.deliveryStatuses;
  }

  if (params.userIds) {
    if (params.userIds.length === 0) delete params.userIds;
  }

  if ((params.userIds as any) === false) {
    delete params.userIds;
  }

  if (params.date) {
    if (!params.date.from || !params.date.to) delete params.date;
  }

  if (!params.orderNumber) {
    delete params.orderNumber;
  }

  if (!params.productId) {
    delete params.productId;
  }

  if (!params.shipType) {
    delete params.shipType;
  }

  return (await post<OrderList>("/admin/order/list", params)).data;
}

async function getOrder(orderId: string) {
  return (await get<Order>(`/admin/order/get?orderId=${orderId}`)).data;
}

async function getOrderDeliveryStatuses(orderId: string) {
  return (await get<{ deliveryStatuses: string[] }>(`/admin/order/getDeliveryStatuses?orderId=${orderId}`)).data;
}

async function downloadOrderInvoicePdf(orderId: string, retailShopId: string): Promise<Blob> {
  const url = `/admin/order/printInvoicePdf?orderId=${orderId}&retailShopId=${retailShopId}`;
  const headers = {
    "x-access-token": getLoginToken()!,
    "shop-id": getShopId()!,
    locale: "en",
  };

  const response = await fetch(process.env.NEXT_PUBLIC_URL_ROOT + url, {
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: "Unknown error" }));
    throw new Error(`Failed to download PDF: ${errorData.message || response.statusText}`);
  }

  return await response.blob();
}

async function updateOrder({ orderId, data, queryClient }: { orderId: string; data: any; queryClient: QueryClient }) {
  await post(`/admin/order/update?orderId=${orderId}`, data);

  queryClient.invalidateQueries({ queryKey: ["orderSingle", orderId] });
  queryClient.invalidateQueries({ queryKey: ["orders"] });
}

interface UpdateShipAddrSf {
  shipType: ShipTypeOption;
  sfCode: string;
  sfRecipientName: string;
  sfRecipientTelCountryCode: string;
  sfRecipientTel: string;
}

interface UpdateShipAddrBasic {
  shipType: ShipTypeOption;
  addr: any;
}

interface UpdateShipAddrPickup {
  shipType: ShipTypeOption;
  pickupAddrId: string;
}

async function updateShipAddr({
  orderId,
  data,
  shipOptionId,
  queryClient,
}: {
  orderId: string;
  data: UpdateShipAddrSf | UpdateShipAddrBasic | UpdateShipAddrPickup;
  shipOptionId: string;
  queryClient: QueryClient;
}) {
  await post(`/admin/order/updateShipAddr?orderId=${orderId}`, { ...data, shipOptionId });

  queryClient.invalidateQueries({ queryKey: ["orderSingle", orderId] });
  queryClient.invalidateQueries({ queryKey: ["orders"] });
}

async function createNote(data: any) {
  return await post<OrderNote>("/admin/order/createNote", data);
}

async function updateNote(noteId: string, data: any) {
  await post(`/admin/order/updateNote?noteId=${noteId}`, data);
}

async function deleteNote(noteId: string, orderId: string) {
  await post(`/admin/order/deleteNote?noteId=${noteId}&orderId=${orderId}`, {});
}

async function createAdocItem(data: any) {
  return await post<OrderNote>("/admin/order/createAdocItem", data);
}

async function updateAdocItem(adocId: string, data: any) {
  await post(`/admin/order/updateAdocItem?adocId=${adocId}`, data);
}

async function deleteAdocItem(adocId: string, orderId: string) {
  await post(`/admin/order/deleteAdocItem?adocId=${adocId}&orderId=${orderId}`, {});
}

async function resendEmail(data: { orderId: string; mailTemplateType: string }) {
  await post(`/admin/order/resendEmail`, data);
}

function getOrderStatusLocale(orderStatus: OrderStatus, { t, msg }: Translation) {
  switch (orderStatus) {
    case "pending":
      return t(msg.order.pending);
    case "processing":
      return t(msg.order.processing);
    case "shipped":
      return t(msg.order.shipped);
    case "delivered":
      return t(msg.order.delivered);
    case "returned":
      return t(msg.order.returned);
    case "cancelled":
      return t(msg.order.cancelled);
    default:
      return null;
  }
}

function getPaymentStatusLocale(paymentStatus: PaymentStatus, { t, msg }: Translation) {
  switch (paymentStatus) {
    case "awaiting":
      return t(msg.payment.awaiting);
    case "deposit":
      return t(msg.payment.deposit);
    case "partialPayment":
      return t(msg.payment.partialPayment);
    case "paid":
      return t(msg.payment.paid);
    case "refunded":
      return t(msg.payment.refunded);
    default:
      return null;
  }
}

function getDeliveryStatusLocale(deliveryStatus: string, { t, msg }: Translation) {
  switch (deliveryStatus) {
    case "scheduled":
      return t(msg.delivery.scheduled);
    case "preparing":
      return t(msg.delivery.preparing);
    case "onDelivery":
      return t(msg.delivery.onDelivery);
    case "canPickup":
      return t(msg.delivery.canPickup);
    case "delivered":
      return t(msg.order.delivered);
    case "cancelled":
      return t(msg.delivery.cancelled);
    default:
      return null;
  }
}

function getGroupByLocale(groupBy: ReportExtra, { t, msg }: Translation) {
  switch (groupBy) {
    case "customer":
      return t(msg.order.customer);
    case "date":
      return t(msg.order.date);
    case "order":
      return t(msg.order.orders);
    case "product":
      return t(msg.product.product);
    default:
      return null;
  }
}

function getPayTypeLocale(payType: PayType, { t, msg }: Translation) {
  switch (payType) {
    case "cod":
      return t(msg.payment.cod);
    case "paypal":
      return t(msg.payment.paypal);
    case "stripe":
      return t(msg.payment.stripe);
    case "kpay":
      return t(msg.pay.kpay);
    default:
      return null;
  }
}

async function createTestOrders() {
  return await post("/test/createOrdersAdmin", {});
}

async function deleteProduct(productId: string, orderId: string) {
  await post(`/admin/order/deleteProduct?orderId=${orderId}&orderProductId=${productId}`, {});
}

async function addOrderProduct(orderId: string, data: any) {
  await post(`/admin/order/addProduct?orderId=${orderId}`, data);
}

async function updateOrderProduct(orderId: string, productId: string, data: any) {
  await post(`/admin/order/updateProduct?orderId=${orderId}&productId=${productId}`, data);
}

async function updateOrderSummary(orderId: string, data: any) {
  await post(`/admin/order/updateOrderSummary?orderId=${orderId}`, data);
}

async function adminCreateOrder(data: ManualOrderFormData) {
  return await post("/admin/order/adminCreateOrder", data);
}

async function adminGeneratePaymentLink(orderId: string) {
  await post(`/admin/order/generatePaymentLink?orderId=${orderId}`, {});
}

async function updateDeliveryNote(orderId: string, deliveryNote: string, queryClient: QueryClient) {
  await post("/admin/order/updateDeliveryNote", { orderId, deliveryNote });

  queryClient.invalidateQueries({ queryKey: ["orderSingle", orderId] });
  queryClient.invalidateQueries({ queryKey: ["orders"] });
  queryClient.invalidateQueries({ queryKey: ["pendingLogisticItems"] });
}

async function updateDeliveryRecycle(orderId: string, deliveryHasRecycle: boolean, queryClient: QueryClient) {
  await post("/admin/order/updateDeliveryRecycle", { orderId, deliveryHasRecycle });

  queryClient.invalidateQueries({ queryKey: ["orderSingle", orderId] });
  queryClient.invalidateQueries({ queryKey: ["orders"] });
  queryClient.invalidateQueries({ queryKey: ["pendingLogisticItems"] });
}

async function updateDeliveryFrontDoorDropOff(orderId: string, deliveryFrontDoorDropOff: boolean, queryClient: QueryClient) {
  await post("/admin/order/updateDeliveryFrontDoorDropOff", { orderId, deliveryFrontDoorDropOff });

  queryClient.invalidateQueries({ queryKey: ["orderSingle", orderId] });
  queryClient.invalidateQueries({ queryKey: ["orders"] });
  queryClient.invalidateQueries({ queryKey: ["pendingLogisticItems"] });
}

async function adminReleaseInventoryManually(orderId: string) {
  await post(`/admin/order/releaseInventory?orderId=${orderId}`, {});
}

async function adminHoldInventoryManually(orderId: string) {
  await post(`/admin/order/holdInventory?orderId=${orderId}`, {});
}

async function updatePayment(
  orderId: string,
  amountReceived: number,
  paymentName: string,
  paymentPortion: string,
  paymentId: string
) {
  await post("/admin/order/updatePayment", { orderId, amountReceived, paymentName, paymentPortion, paymentId });
}

async function createPayment(orderId: string, amountReceived: number, paymentName: string, paymentPortion: string) {
  await post("/admin/order/createPayment", { orderId, amountReceived, paymentName, paymentPortion });
}

async function deletePayment(orderId: string, paymentId: string) {
  await post("/admin/order/deletePayment", { orderId, paymentId });
}

async function kpayRefund(orderId: string, paymentId: string, refundAmount: number) {
  return (await post<{ code?: number; mocked?: boolean }>("/admin/order/kpayRefund", { orderId, paymentId, refundAmount }))
    .data;
}

async function updatePaymentProof(files: FileList, orderId: string, paymentId: string, queryClient: QueryClient) {
  const formData = new FormData();

  for (const file of files) {
    formData.append("media", file);
  }

  const res = await postFormData(`/admin/order/uploadPaymentProof?orderId=${orderId}&paymentId=${paymentId}`, formData);

  queryClient.invalidateQueries({ queryKey: ["mediaList"] });
  return res.data;
}

const obj = {
  loadOrders,
  getOrder,
  getOrderDeliveryStatuses,
  downloadOrderInvoicePdf,
  updateOrder,
  updateShipAddr,
  createNote,
  updateNote,
  deleteNote,
  createAdocItem,
  updateAdocItem,
  deleteAdocItem,
  resendEmail,
  getOrderStatusLocale,
  getPaymentStatusLocale,
  getDeliveryStatusLocale,
  getPayTypeLocale,
  getGroupByLocale,
  createTestOrders,
  updatePayment,
  createPayment,
  deletePayment,
  kpayRefund,
  updatePaymentProof,
  deleteProduct,
  addOrderProduct,
  updateOrderProduct,
  updateOrderSummary,
  adminCreateOrder,
  adminGeneratePaymentLink,
  updateDeliveryNote,
  updateDeliveryRecycle,
  updateDeliveryFrontDoorDropOff,
  adminReleaseInventoryManually,
  adminHoldInventoryManually,
};

export default obj;
